import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1505260540486-10233d451528?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHx3YXJtJTIwbGlnaHQlMjBzcGlyaXR1YWwlMjByZWZsZWN0aW9ufGVufDB8fHx8MTc4NTM0ODk2Nnww&ixlib=rb-4.1.0&q=85";

export default function AuthForm({ mode }) {
  const isLogin = mode === "login";
  const { login, register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/journal";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back.");
      } else {
        await register(email, password, name);
        toast.success("Your journey begins.");
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid={`${mode}-page`} className="min-h-[calc(100vh-73px)] grid lg:grid-cols-2">
      {/* Visual side */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Sacred reflection" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#110D14] via-[#110D14]/70 to-[#110D14]/40" />
        <div className="absolute bottom-16 left-12 right-12 z-10">
          <p className="italic-serif text-3xl text-[#D4AF37] leading-snug">
            “You will know the truth, and the truth will set you free.”
          </p>
          <p className="mt-3 text-[#A8A29E]">— John 8:32</p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-md glass rounded-2xl p-8 sm:p-10 gold-glow fade-up">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-3">
            {isLogin ? "Return inward" : "Begin the journey"}
          </p>
          <h1 className="serif text-4xl text-[#F5F1E7] font-light">
            {isLogin ? "Welcome back" : "Create your space"}
          </h1>
          <p className="mt-2 text-[#A8A29E]">
            {isLogin ? "Enter to continue your reflections." : "Your truth journal awaits."}
          </p>

          <div className="mt-8 space-y-4">
            {!isLogin && (
              <Field label="Name">
                <input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What shall we call you?"
                  className="auth-input"
                />
              </Field>
            )}
            <Field label="Email">
              <input
                data-testid="email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="auth-input"
              />
            </Field>
            <Field label="Password">
              <input
                data-testid="password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="auth-input"
              />
            </Field>
            {isLogin && (
              <div className="text-right -mt-1">
                <Link
                  data-testid="forgot-password-link"
                  to="/forgot-password"
                  className="text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
                >
                  Forgot password?
                </Link>
              </div>
            )}
          </div>

          {error && (
            <p data-testid="auth-error" className="mt-4 text-sm text-[#C1272D]">
              {error}
            </p>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="mt-7 w-full rounded-full bg-[#D4AF37] text-[#110D14] px-7 py-3.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? "Please wait..." : isLogin ? "Enter" : "Create account"}
          </button>

          <p className="mt-6 text-center text-sm text-[#A8A29E]">
            {isLogin ? "New here? " : "Already have a space? "}
            <Link
              data-testid="auth-switch-link"
              to={isLogin ? "/register" : "/login"}
              className="text-[#D4AF37] hover:underline"
            >
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </form>
      </div>

      <style>{`
        .auth-input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(245,241,231,0.1);
          border-radius: 0.75rem;
          padding: 0.75rem 1rem;
          color: #F5F1E7;
          outline: none;
          transition: border-color 0.3s ease;
        }
        .auth-input::placeholder { color: rgba(168,162,158,0.5); }
        .auth-input:focus { border-color: rgba(212,175,55,0.6); }
      `}</style>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-[#A8A29E] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
